import { TraversalViewModel } from '@ardoq/graph';
import { t, type I18nLocale } from '@ardoq/i18n';
import { RelationshipsNode, RelationshipsViewSettings } from '../types';
import {
  SettingsConfig,
  SettingsType,
  ToggleConfig,
} from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { showOnlyConnectedComponents } from '@ardoq/settings-bar';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { relationshipsTraversalMenu } from 'tabview/relationsD3View/menus';
import {
  bundleRelationshipsHandler,
  getCollapseAllButtonConfig,
  getExpandAllButtonConfig,
  notifyHighlightDisconnectedComponentsChanged,
} from '../sharedControlsConfigs';
import { ViewIds } from '@ardoq/api-types';

export type WorkspaceModeRelationshipsSettingsControlsDependencies = Pick<
  TraversalViewModel,
  | 'traversedIncomingReferenceTypes'
  | 'traversedOutgoingReferenceTypes'
  | 'referenceTypes'
> & {
  viewSettings: RelationshipsViewSettings;
  quickPerspectiveReferenceTypeFilters: Set<string> | null;
  rootNode: RelationshipsNode | null;
  activeGroups: string[];
  viewInstanceId: string;
};

type ToWorkspaceModeRelationshipsViewModifiersArgs = {
  dependencies: WorkspaceModeRelationshipsSettingsControlsDependencies;
  i18nLocale: I18nLocale;
};

export const toWorkspaceModeRelationshipsViewModifiers = ({
  dependencies: {
    viewSettings,
    traversedIncomingReferenceTypes,
    traversedOutgoingReferenceTypes,
    referenceTypes,
    quickPerspectiveReferenceTypeFilters,
    activeGroups,
    rootNode,
    viewInstanceId,
  },
  i18nLocale,
}: ToWorkspaceModeRelationshipsViewModifiersArgs): SettingsConfig[] => {
  const { highlightDisconnectedComponents, bundleRelationships } = viewSettings;

  const bundleRelationshipsConfig = {
    id: 'bundleRelationships',
    label: t(
      i18nLocale,
      'relationsD3View:settingsBar.combineReferenceLines',
      'Combine reference lines'
    ),
    iconName: IconName.EDGE_BUNDLING,
    isActive: bundleRelationships,
    type: SettingsType.TOGGLE,
    onViewSettingsUpdate: bundleRelationshipsHandler(!bundleRelationships),
  } satisfies ToggleConfig;

  const hasNoGroups = activeGroups.length === 0;

  const collapseAllButtonConfig = getCollapseAllButtonConfig({
    viewSettings,
    isDisabled: hasNoGroups,
    rootNode,
    viewInstanceId,
    i18nLocale,
  });

  const expandAllButtonConfig = getExpandAllButtonConfig({
    viewSettings,
    isDisabled: hasNoGroups,
    viewInstanceId,
    i18nLocale,
  });

  const traversalMenu = relationshipsTraversalMenu({
    viewSettings,
    traversedIncomingReferenceTypes,
    traversedOutgoingReferenceTypes,
    referenceTypes,
    quickPerspectiveReferenceTypeFilters,
    viewId: ViewIds.RELATIONSHIPS_3,
    i18nLocale,
  });

  const showAllConnectedComponentsConfig = showOnlyConnectedComponents(
    viewSettings.showOnlyConnectedComponents,
    onViewSettingsUpdate
  );

  const highlightDisconnectedComponentsConfig = {
    id: 'highlightDisconnectedComponents',
    label: t(
      i18nLocale,
      'relationsD3View:settingsBar.highlightDisconnected',
      'Highlight disconnected components'
    ),
    iconName: IconName.BULLSEYE,
    isActive: highlightDisconnectedComponents,
    type: SettingsType.TOGGLE,
    onViewSettingsUpdate: () =>
      notifyHighlightDisconnectedComponentsChanged({
        highlightDisconnectedComponents,
        persistent: true,
      }),
  } satisfies ToggleConfig;

  return [
    traversalMenu,
    bundleRelationshipsConfig,
    collapseAllButtonConfig,
    expandAllButtonConfig,
    showAllConnectedComponentsConfig,
    highlightDisconnectedComponentsConfig,
  ];
};
